import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { ALL_CARDS } from "../data/cards";
import { CardGenre, CardPack, Card as CardType } from "../types";
import Card from "./Card";
import { CardPicker } from "./extras/CardPicker";
import { CocktailGenerator } from "./extras/CocktailGenerator";
import { CoinFlipper } from "./extras/CoinFlipper";
import { RandomNumberGenerator } from "./extras/RandomNumberGenerator";

interface GameProps {
  mode: CardPack;
}

interface GameState {
  deck: CardType[];
  discard: CardType[];
  currentCard: CardType | null;
}

const countGenre = (discard: CardType[], cardGenre: CardGenre) => {
  return discard.filter((card) => card.genre === cardGenre).length;
};

const Game: React.FC<GameProps> = ({ mode }) => {
  // Filter cards by mode
  const filteredCards = ALL_CARDS.filter((card) => {
    if (mode === "pub") {
      return card?.pub;
    }
    if (mode === "nsfw") {
      return card.genre === "chaos";
    }
    return true;
  });

  const filteredCardsWithRepeatMapped = filteredCards.map((card) => {
    const descriptionIncludesClothes = ["clothes", "clothing"].some((word) =>
      card.description.includes(word)
    );

    const isClothes = card.activity === "clothes" || descriptionIncludesClothes;

    if (mode === "clothes" && isClothes) {
      return {
        ...card,
        repeat: (card.repeat || 1) * 15,
      };
    }

    if (mode === "pub" && isClothes) {
      return {
        ...card,
        repeat: 0,
      };
    }
    return {
      ...card,
      repeat: card.repeat || 1,
    };
  });

  const filteredWithRepeats = filteredCardsWithRepeatMapped.flatMap((card) =>
    Array(card.repeat).fill(card)
  );

  const shuffledCards = [...filteredWithRepeats].reduce(
    (acc, _, i, arr) => {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    },
    [...filteredWithRepeats]
  );

  // Initialize state from localStorage or default values
  const [deck, setDeck] = useState<CardType[]>(() => {
    const savedState = localStorage.getItem(`gameState_${mode}`);
    if (savedState) {
      const parsedState: GameState = JSON.parse(savedState);
      return parsedState.deck;
    }
    return shuffledCards;
  });

  const [discard, setDiscard] = useState<CardType[]>(() => {
    const savedState = localStorage.getItem(`gameState_${mode}`);
    if (savedState) {
      const parsedState: GameState = JSON.parse(savedState);
      return parsedState.discard;
    }
    return [];
  });

  const [currentCard, setCurrentCard] = useState<CardType | null>(() => {
    const savedState = localStorage.getItem(`gameState_${mode}`);
    if (savedState) {
      const parsedState: GameState = JSON.parse(savedState);
      return parsedState.currentCard;
    }
    return null;
  });

  const [showHistory, setShowHistory] = useState(false);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    const gameState: GameState = {
      deck,
      discard,
      currentCard,
    };
    localStorage.setItem(`gameState_${mode}`, JSON.stringify(gameState));
  }, [deck, discard, currentCard, mode]);

  const handleDraw = () => {
    if (deck.length === 0) return;
    const [next, ...rest] = deck;
    if (currentCard) setDiscard([currentCard, ...discard]);
    setCurrentCard(next);
    setDeck(rest);
  };

  const [isGambleMenuOpen, setIsGambleMenuOpen] = useState(true);

  return (
    <div className="flex flex-col items-center gap-6">
      <div className="flex flex-col gap-6 items-center">
        <div className="fixed bottom-0 right-0 text-white opacity-5">
          D{countGenre(discard, "dare")}_T{countGenre(discard, "truth")}_Q
          {countGenre(discard, "question")}_C{countGenre(discard, "curse")}_G
          {countGenre(discard, "gamble")}_W{countGenre(discard, "wyr")}_B
          {countGenre(discard, "battle")}_W{countGenre(discard, "wildcard")}_C
          {countGenre(discard, "chaos")}_{discard.length}/{deck.length}
        </div>
        <AnimatePresence>
          {(currentCard?.extras?.includes("coinFlip") ||
            currentCard?.extras?.includes("randomNumber") ||
            currentCard?.extras?.includes("rollForCocktail") ||
            currentCard?.extras?.includes("pickFromCardDeck")) && (
            <motion.div
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 100, opacity: 0 }}
              className="flex gap-4 flex-col fixed bottom-0 right-0 p-6 bg-[#222]/60 backdrop-blur-sm rounded-tl-lg z-50"
            >
              <button
                onClick={() => setIsGambleMenuOpen(!isGambleMenuOpen)}
                className="absolute top-2 right-2 text-white hover:text-gray-300 text-3xl"
              >
                {isGambleMenuOpen ? "×" : "☰"}
              </button>

              {isGambleMenuOpen && (
                <>
                  {currentCard?.extras?.includes("coinFlip") && <CoinFlipper />}
                  {currentCard?.extras?.includes("randomNumber") && (
                    <RandomNumberGenerator />
                  )}
                  {currentCard?.extras?.includes("rollForCocktail") && (
                    <CocktailGenerator />
                  )}
                  {currentCard?.extras?.includes("pickFromCardDeck") && (
                    <CardPicker />
                  )}
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        {!currentCard ? (
          <>
            <h1 className="text-white text-2xl font-bold">
              {mode.charAt(0).toUpperCase() + mode.slice(1)} Mode
            </h1>
          </>
        ) : (
          <></>
        )}
        {currentCard && (
          <div className="transition-all duration-500 ease-out animate-fadein">
            <Card card={currentCard} />
          </div>
        )}
        <button
          className="bg-[#0a0a2a] border-2 border-[#3a3a7a] text-white font-bold py-3 px-10 rounded-2xl shadow-lg  transition-all duration-200 text-lg hover:bg-[#18184a] hover:border-[#5a5ab0] focus:outline-none focus:ring-2 focus:ring-[#5a5ab0]"
          onClick={handleDraw}
        >
          {!currentCard ? "Start Game" : "Draw Card"}
        </button>

        {discard.length > 0 && (
          <>
            <div
              className="bg-black/30 rounded-lg  w-full  text-white text-center py-2 max-w-[600px] cursor-pointer"
              onClick={() => setShowHistory(!showHistory)}
            >
              {showHistory ? "Hide History" : "Show History"}
            </div>
            {showHistory ? (
              <div className=" bg-black/30 rounded-lg  w-full">
                <table className="w-full text-white">
                  <tbody>
                    {discard?.map((card, index) => (
                      <tr
                        key={index}
                        className={`border-white/20 ${
                          discard.indexOf(card) % 2 === 1 ? "bg-white/5" : ""
                        }`}
                      >
                        <td className="py-2 px-4">
                          #{discard?.length - index}
                        </td>
                        <td className="py-2 px-4">
                          {card.penaltyCost || "--"}
                        </td>
                        <td className="py-2 px-4">{card.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <></>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Game;
